<script setup>
import { ref, computed } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faArrowDown, faArrowUp, faReceipt } from '@fortawesome/free-solid-svg-icons'
import { startOfMonth, endOfMonth, subMonths } from 'date-fns'

import { useIntelichekFetch } from '@/lib/intelichek-fetch.js'

import { formatDate, formatMonth, getYearMonthListSince } from '@/lib/date_helper.js'

import DropdownMenu from '@/components/ui/dropdown-menu.vue'
import SkeletonLoader from '@/components/ui/skeleton_loader.vue'

import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const filterOptions = ref([
  {
    value: 'last_30_days',
    date_range: [subMonths(new Date(), 1), new Date()]
  },
  {
    value: 'last_6_months',
    date_range: [subMonths(new Date(), 6), new Date()]
  },
  {
    value: 'all_time',
    date_range: null
  }
])
const filter = ref(filterOptions.value[0])

const ledgerUrl = computed(() => {
  const url = new URL(`${import.meta.env.VITE_BACKEND_URL}/api/v2/servicechek/user_quota_ledgers`)
  if (filter.value.date_range) {
    url.searchParams.append('query[created_at_gt]', filter.value.date_range[0].toISOString())
    url.searchParams.append('query[created_at_lt]', filter.value.date_range[1].toISOString())
  }
  url.searchParams.append('items', 1_000)
  return url.href
})

const {
  isFetching: isLedgerLoading,
  data: ledger
} = useIntelichekFetch(ledgerUrl, { refetch: true, immediate: true }).json()

const sortedLedger = computed(() => {
  if (!ledger.value?.data) return null
  return {
    ...ledger.value,
    data: [...ledger.value.data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  }
})



</script>

<template>
  <div class="vh-screen flex flex-col max-w-4xl mx-auto">
    <!-- Header -->
    <div class="flex flex-row items-center justify-between mb-8 pt-8">
      <h1 class="font-bold text-3xl text-gray-900">{{ t('settings.quote_history.name') }}</h1>
      <dropdown-menu>
        <template #link>
          <div class="bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
            {{ t('settings.quote_history.month_dropdown', { filter: t(`settings.quote_history.${filter.value}`) }) }}
          </div>
        </template>
        <template #dropdown>
          <li class="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 cursor-pointer" role="menuitem"
              v-for="filterOption in filterOptions"
              @click="filter = filterOption">
            {{ t(`settings.quote_history.${filterOption.value}`) }}
          </li>
        </template>
      </dropdown-menu>
    </div>

    <!-- Content -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div v-if="isLedgerLoading" class="p-6">
        <SkeletonLoader type="list" />
      </div>
      
      <div v-else-if="sortedLedger?.data?.length" class="divide-y divide-gray-100">
        <div class="flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
             v-for="item in sortedLedger.data" :key="item.id">
          <!-- Icon and Description -->
          <div class="flex items-center space-x-4">
            <div class="flex-shrink-0">
              <div :class="[
                'w-10 h-10 rounded-full flex items-center justify-center',
                item.entry_type === 'added' ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'
              ]">
                <FontAwesomeIcon 
                  :icon="item.entry_type === 'added' ? faArrowUp : faArrowDown" 
                  class="w-4 h-4"
                />
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900">
                {{ t(`settings.quote_history.source_${item.source}`) }}
              </p>
              <p class="text-sm text-gray-500">
                {{ formatDate(item.created_at) }}
              </p>
            </div>
          </div>
          
          <!-- Amount -->
          <div class="flex-shrink-0">
            <span :class="[
              'text-sm font-semibold',
              item.entry_type === 'added' ? 'text-blue-600' : 'text-orange-600'
            ]">
              {{ item.entry_type === 'added' ? '+' : '-' }}{{ item.amount }}
            </span>
          </div>
        </div>
      </div>
      
      <div v-else class="p-12 text-center">
        <div class="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <FontAwesomeIcon :icon="faReceipt" class="w-6 h-6 text-gray-400" />
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">{{ t('settings.quote_history.no_transactions_found') }}</h3>
        <p class="text-gray-500">{{ t('settings.quote_history.no_transactions_yet') }}</p>
      </div>
    </div>
  </div>
</template>


<style lang="scss" scoped>
@import '@/assets/styles/views/settings/quote_history';

</style>
